import { word } from "@modules/core/utils/i18n"
import { TransactionTotalsEntity } from "@modules/finances/entities/transactionTotalsEntity"
import { usePeriodParam } from "@modules/finances/hooks/usePeriod"
import { useTransactionTotals } from "@modules/finances/hooks/useTransactionTotals"
import { TransactionStatus } from "@modules/finances/types/financesType"
import { HUText } from "@modules/ui/components/huText"
import { monthYearDateFormat } from "@modules/utils/dateUtils"
import { useQueryParam } from "@modules/utils/navigation/useQueryParam"
import dayjs from "dayjs"
import { useMemo } from "react"

type Props = {
  filters: (TransactionStatus | "ALL")[]
} & React.HTMLAttributes<HTMLDivElement>

export const TransactionTableFilter: React.FC<Props> = ({ filters, ...props }) => {
  const [transactionsTab, setTransactionsTab] = useQueryParam("tab")
  const defaultValue = useMemo(() => dayjs().format(monthYearDateFormat), [])
  const [period] = usePeriodParam()
  const { data: transactionTotals } = useTransactionTotals(period ?? defaultValue)
  const defaultClassNames = "flex flex-row gap-2"

  return (
    <div {...props} className={props.className ? props.className : defaultClassNames}>
      {filters.map((filter) => (
        <button onClick={() => setTransactionsTab(filter)} key={filter}>
          <div
            className={`gap-2 flex flex-row rounded-full px-3 py-2 transition-colors transition-duration-150 hover:surface-100 ${transactionsTab === filter || (!transactionsTab && filter === "ALL") ? "bg-neutralColor-100" : ""}`}
          >
            <HUText fontStyle="LL">{getTransactionStatus({ status: filter, plural: true })}</HUText>
            {!!transactionTotals && <HUText fontStyle="BM">({getTransactionTotals(filter, transactionTotals)})</HUText>}
          </div>
        </button>
      ))}
    </div>
  )
}

export const getTransactionTotals = (
  filter: TransactionStatus | "ALL",
  transactionTotals: TransactionTotalsEntity,
): number => {
  const totals: { [key in TransactionStatus]: number } = {
    SUCCEEDED: transactionTotals.succeededTransactionsNumber,
    PENDING: transactionTotals.pendingTransactionsNumber,
    FAILED: transactionTotals.failedTransactionsNumber,
    DISPUTED: transactionTotals.disputedTransactionsNumber,
    REFUNDED: transactionTotals.refundedTransactionsNumber,
  }
  return filter === "ALL" ? transactionTotals.totalTransactionsNumber : totals[filter]
}

const getTransactionStatus = (param: { status: TransactionStatus | "ALL"; plural?: boolean; refunded?: boolean }) => {
  const { status, plural, refunded } = param
  const wordStatus: { [key in TransactionStatus]: string | undefined } = {
    SUCCEEDED: word("finances.page.table.status.succeded", { count: plural ? 2 : 1 }),
    PENDING: word("finances.page.table.status.pending", { count: plural ? 2 : 1 }),
    FAILED: word("finances.page.table.status.failed", { count: plural ? 2 : 1 }),
    DISPUTED: word("finances.page.table.status.disputed", { count: plural ? 2 : 1 }),
    REFUNDED: word("finances.page.table.status.refunded", { count: plural ? 2 : 1 }),
  }

  if (refunded) return word("finances.page.table.status.refunded", { count: plural ? 2 : 1 })
  return status === "ALL" ? word("finances.page.filter.all") : wordStatus[status]
}
